/**
 * cdn-domain-tab-registry.ts — Static tab registry for the CDN domain detail page.
 *
 * Lists all 23 CDN domain tabs (slugs, labels, sections) defined in the Wave A
 * build spec. Kept separate from the page component so the page stays under the
 * 200-LOC guideline and A2–A4 implementors can verify the full list at a glance.
 */

/* ── All 23 tab slugs ────────────────────────────────────────────────────── */

/**
 * Canonical tab value list — matches the ?tab= URL param.
 * Order matters: it controls the visual order of triggers.
 */
export const CDN_TABS = [
  // A1 — Overview (this wave)
  'overview',
  // A2 — Caching (3 tabs)
  'cache',
  'upstream',
  'origin-rewrite',
  // A3 — Rules (3 tabs)
  'header-rules',
  'error-pages',
  'redirects',
  // A3 — Delivery (2 tabs)
  'purge',
  'ssl',
  // A4 — Optimization + Operations
  'optimization',
  'operations',
  // A4 — Security (10 tabs)
  'security/waf',
  'security/firewall-rules',
  'security/waf-analytics',
  'security/rate-limiting',
  'security/access-control',
  'security/cors',
  'security/security-headers',
  'security/verified-bots',
  'security/bot-management',
  'security/bot-management-analytics',
] as const;

export type CdnTab = (typeof CDN_TABS)[number];

export const DEFAULT_TAB: CdnTab = 'overview';

/* ── Human-readable labels ───────────────────────────────────────────────── */

export const CDN_TAB_LABEL: Record<CdnTab, string> = {
  overview: 'Overview',
  cache: 'Cache',
  upstream: 'Origin',
  'origin-rewrite': 'Origin Rewrite',
  'header-rules': 'Header Rules',
  'error-pages': 'Error Pages',
  redirects: 'Redirects',
  purge: 'Purge & Warm',
  ssl: 'SSL / TLS',
  optimization: 'Optimization',
  operations: 'Operations',
  'security/waf': 'WAF',
  'security/firewall-rules': 'Firewall Rules',
  'security/waf-analytics': 'WAF Analytics',
  'security/rate-limiting': 'Rate Limiting',
  'security/access-control': 'Access Control',
  'security/cors': 'CORS',
  'security/security-headers': 'Security Headers',
  'security/verified-bots': 'Verified Bots',
  'security/bot-management': 'Bot Management',
  'security/bot-management-analytics': 'Bot Mgmt Analytics',
};

/* ── Section groupings (visual separators in the trigger list) ───────────── */

export interface CdnTabSection {
  /** Empty string = no section separator rendered before these triggers. */
  label: string;
  tabs: CdnTab[];
}

export const CDN_TAB_SECTIONS: CdnTabSection[] = [
  { label: '', tabs: ['overview'] },
  { label: 'Caching', tabs: ['cache', 'upstream', 'origin-rewrite'] },
  { label: 'Rules', tabs: ['header-rules', 'error-pages', 'redirects'] },
  { label: 'Delivery', tabs: ['purge', 'ssl'] },
  { label: '', tabs: ['optimization', 'operations'] },
  {
    label: 'Security',
    tabs: [
      'security/waf',
      'security/firewall-rules',
      'security/waf-analytics',
      'security/rate-limiting',
      'security/access-control',
      'security/cors',
      'security/security-headers',
      'security/verified-bots',
      'security/bot-management',
      'security/bot-management-analytics',
    ],
  },
];

/* ── Stub tabs (A2–A4) — all tabs except overview ───────────────────────── */

export const CDN_STUB_TABS = CDN_TABS.filter((t): t is Exclude<CdnTab, 'overview'> => t !== 'overview');
